import React, { useState, useEffect, useRef } from 'react';
import { api, streamChat } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Bot, Send, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function ChatbotTab() {
  const { t } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef();

  useEffect(() => {
    api.get('/ai/chat/history').then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput('');
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', content: msg }]);
    setStreaming(true);
    let assistantText = '';
    const assistantId = `a-${Date.now()}`;
    setMessages((m) => [...m, { id: assistantId, role: 'assistant', content: '' }]);

    await streamChat(msg, {
      onToken: (tok) => {
        assistantText += tok;
        setMessages((m) => m.map((x) => (x.id === assistantId ? { ...x, content: assistantText } : x)));
      },
      onDone: () => setStreaming(false),
      onError: (err) => {
        setStreaming(false);
        toast.error(err);
        setMessages((m) => m.filter((x) => x.id !== assistantId || x.content));
      },
      onLimit: () => setStreaming(false),
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border elev-1 flex flex-col h-[60vh]" data-testid="ai-chatbot-container">
      {/* header bar */}
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Bot size={14} className="text-[hsl(205,100%,56%)]" /> FitBot · AI fitness expert
        </span>
        <span className="text-xs font-medium text-[hsl(205,100%,56%)] flex items-center gap-1" data-testid="ai-chat-remaining-label">
          <InfinityIcon size={14} /> Unlimited
        </span>
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">
            <Bot size={36} className="mx-auto mb-3 opacity-40 text-[hsl(205,100%,56%)]" />
            <p className="text-sm">Ask me anything about training, nutrition or recovery!</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {['How much protein do I need?', 'Best home chest workout?', 'How to fix sore muscles?'].map((q) => (
                <button key={q} onClick={() => setInput(q)} className="text-xs px-3 py-1.5 rounded-full border border-[hsl(205,100%,56%)]/30 text-[hsl(205,100%,56%)] hover:bg-[hsl(205,100%,56%)]/10">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-secondary border border-border'
                : 'bg-[hsl(205,100%,56%)]/8 border border-[hsl(205,100%,56%)]/25'
            }`}>
              {m.content || <span className="flex gap-1"><span className="typing-dot">●</span><span className="typing-dot" style={{ animationDelay: '0.2s' }}>●</span><span className="typing-dot" style={{ animationDelay: '0.4s' }}>●</span></span>}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* input */}
      <div className="p-3 border-t border-border flex gap-2">
        <Input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask FitBot..." disabled={streaming}
          onKeyDown={(e) => e.key === 'Enter' && send()} />
        <Button data-testid="ai-chat-send-button" onClick={send} disabled={streaming || !input.trim()}
          className="bg-[hsl(205,100%,56%)] text-black hover:bg-[hsl(205,100%,62%)]">
          <Send size={15} />
        </Button>
      </div>
    </div>
  );
}
